<template>
    <div class="forms__field_input--table-repeater">
        <table class="forms__field_input--table-repeater__table">
            <thead class="forms__field_input--table-repeater__thead">
                <tr>
                    <th
                        v-for="subfield in fieldsList"
                        :key="subfield.slug"
                        class="forms__field_input--table-repeater__th"
                    >
                        {{ subfield.name }}
                    </th>
                    <th class="forms__field_input--table-repeater__th forms__field_input--table-repeater__actions"></th>
                </tr>
            </thead>
            <tbody class="forms__field_input--table-repeater__tbody">
                <tr
                    v-for="(row, rowIndex) in internalModelValue"
                    :key="row._id"
                    class="forms__field_input--table-repeater__tr"
                >
                    <td
                        v-for="subfield in fieldsList"
                        :key="`${row._id}-${subfield.slug}`"
                        class="forms__field_input--table-repeater__td"
                        :class="{
                            'forms__field_input--table-repeater__td--nested': isNestedType(subfield.type),
                        }"
                    >
                        <Field
                            v-model="row[subfield.slug]"
                            :field="subfield"
                            :options="subfield.options"
                            :size="isNestedType(subfield.type) ? 'base' : 'xs'"
                            label-position="none"
                        />
                    </td>
                    <td class="forms__field_input--table-repeater__td forms__field_input--table-repeater__actions">
                        <button
                            type="button"
                            class="forms__field_input--table-repeater__remove"
                            @click="removeRow(rowIndex)"
                        >
                            <icon icon="hero/outline/trash" class="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                    </td>
                </tr>
            </tbody>
            <tfoot class="forms__field_input--table-repeater__tfoot">
                <tr>
                    <td :colspan="fieldsList.length + 1" class="forms__field_input--table-repeater__tfoot__td">
                        <Button type="button" size="sm" @click="addRow">
                            <icon icon="hero/solid/plus-sm" class="w-4 h-4"></icon>
                        </Button>
                    </td>
                </tr>
            </tfoot>
        </table>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, toRefs } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import type { FieldComponent } from '@src/Common/Components/types';

const modelValue = defineModel({
    type: Array,
});

if (typeof modelValue.value === 'undefined' || modelValue.value === null) {
    modelValue.value = [];
}

const emit = defineEmits(['update:modelValue']);

const props = withDefaults(defineProps<FieldComponent>(), {
    field: () => ({
        name: '',
        slug: '',
        type: 'table_repeater',
        fields: {},
    }),
    tabindex: null,
    size: 'base',
});

const { field } = toRefs(props);

const fieldsList = computed(() => {
    if (!field.value.fields) {
        return [];
    }

    return Object.values(field.value.fields);
});

/**
 * Check if the field type is a nested type (repeater, table_repeater, group)
 */
const isNestedType = (type: string): boolean => {
    return ['repeater', 'table_repeater', 'group'].includes(type);
};

/**
 * Get default value for a field based on its type
 */
const getDefaultValue = (subfield: any): any => {
    if (subfield.default !== undefined) {
        return subfield.default;
    }

    if (isNestedType(subfield.type)) {
        return [];
    }

    return null;
};

/**
 * Initialize row data with proper defaults for all fields
 */
const initializeRowData = (item: any): Record<string, any> => {
    const row: Record<string, any> = {
        _id: uuidv4(),
    };

    if (field.value.fields) {
        Object.keys(field.value.fields).forEach((key) => {
            const subfield = field.value.fields[key];
            const existingValue = item && typeof item === 'object' ? item[key] : undefined;

            if (existingValue !== undefined) {
                row[key] = existingValue;
            } else {
                row[key] = getDefaultValue(subfield);
            }
        });
    }

    return row;
};

const internalModelValue = ref(
    modelValue.value ? [...modelValue.value].map((item) => initializeRowData(item)) : [],
);

const addRow = () => {
    internalModelValue.value.push(initializeRowData(null));
};

const removeRow = (index: number) => {
    internalModelValue.value.splice(index, 1);
};

watch(
    internalModelValue,
    (value) => {
        emit(
            'update:modelValue',
            value.map((item) => {
                const newItem = { ...item };
                delete newItem._id;
                return newItem;
            }),
        );
    },
    { deep: true },
);
</script>
